<template>
  <div>
    <!-- Dialog Attachment -->
    <DialogPrime
      v-model:visible="dialog.visible"
      modal
      style="width: 75rem; margin: 10px;"
      class="mcr-attachment-modal"
    >
      <template #header>
        <div class="d-flex align-items-center gap-3 w-100 pe-3">
          <h5 class="mb-0 fw-bold text-dark">{{ t('Attachment') }}</h5>
          <span v-if="dialog.mcrCode" class="badge bg-light text-dark border px-2 py-1" style="font-size: 0.75rem; font-weight: 600;">
            {{ dialog.mcrCode }}
          </span>
          <small v-if="dialog.error" class="text-danger d-flex align-items-center" style="font-style: italic; font-size: 0.75rem;">
            <i class="fas fa-exclamation-circle me-1"></i> {{ dialog.error }}
          </small>
          <div class="ms-auto">
            <ButtonPrime
              type="button"
              :label="t('Upload')"
              icon="pi pi-upload"
              size="small"
              :loading="dialog.uploading"
              :disabled="readonly || !dialog.mcrCode || dialog.uploading"
              @click="triggerUpload"
            />
            <input
              ref="fileInput"
              type="file"
              class="d-none"
              accept=".pdf,image/*,.xls,.xlsx,.csv,.doc,.docx"
              @change="onFileChange"
            />
          </div>
        </div>
      </template>

      <!-- Progress upload -->
      <div v-if="dialog.uploading" class="mb-3">
        <div class="progress rounded" style="height: 1.5rem;">
          <div
            class="progress-bar bg-primary rounded"
            role="progressbar"
            :style="{ width: dialog.uploadProgress + '%', transition: 'width 0.3s ease' }"
            :aria-valuenow="dialog.uploadProgress"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {{ dialog.uploadProgress }}%
          </div>
        </div>
      </div>

      <div v-if="dialog.loading">
        <DataTable :value="Array(4)" class="p-datatable-sm p-datatable-gridlines" table-style="min-width: 40rem">
          <Column v-for="n in 5" :key="n">
            <template #header><Skeleton width="60%" height="0.9rem" /></template>
            <template #body><Skeleton width="85%" height="1.4rem" /></template>
          </Column>
        </DataTable>
      </div>
      <DataTable
        v-else
        :value="dialog.attachments"
        class="p-datatable-sm p-datatable-gridlines"
        scrollable
        scroll-height="400px"
        table-style="min-width: 40rem"
      >
        <Column header="No." header-class="bg-light text-center" header-style="{ width: '60px' }" :style="{ width: '60px' }">
          <template #body="{ index }">{{ index + 1 }}</template>
        </Column>
        <Column header="File Name" header-class="bg-light">
          <template #body="{ data }">
            <div class="d-flex align-items-center gap-2">
              <i :class="getFileIcon(data)"></i>
              <a
                v-if="data.file_attachment?.id || data.file_code"
                class="text-primary text-decoration-underline"
                style="cursor: pointer;"
                @click="downloadAttachment(data)"
              >
                {{ data.file_attachment?.original_filename || data.file_attachment?.file_name || data.file_code }}
              </a>
              <span v-else>-</span>
            </div>
          </template>
        </Column>
        <Column header="Size" header-class="bg-light" header-style="{ width: '110px' }" :style="{ width: '110px' }">
          <template #body="{ data }">{{ formatFileSize(data.file_attachment?.file_size) }}</template>
        </Column>
        <Column header="Uploaded By" header-class="bg-light" header-style="{ width: '200px' }" :style="{ width: '200px' }">
          <template #body="{ data }">{{ data.user_uploaded?.name || data.uploaded_by || '-' }}</template>
        </Column>
        <Column header="Uploaded At" header-class="bg-light" header-style="{ width: '170px' }" :style="{ width: '170px' }">
          <template #body="{ data }">{{ formatDate(data.created_at) }}</template>
        </Column>
        <Column header-class="bg-light text-center" header-style="{ width: '200px' }" :style="{ width: '200px' }">
          <template #header><div class="centered-column-header">Action</div></template>
          <template #body="{ data }">
            <div class="d-flex justify-content-center gap-1">
              <ButtonPrime
                v-if="isPreviewableFile(data)"
                v-tooltip.top="'Preview'"
                icon="pi pi-eye"
                outlined
                rounded
                severity="info"
                size="small"
                @click="previewAttachment(data)"
              />
              <ButtonPrime
                v-tooltip.top="'Download'"
                icon="pi pi-download"
                outlined
                rounded
                severity="success"
                size="small"
                :disabled="!data.file_attachment?.id && !data.file_code"
                @click="downloadAttachment(data)"
              />
              <ButtonPrime
                v-tooltip.top="'Delete'"
                icon="pi pi-trash"
                outlined
                rounded
                severity="danger"
                size="small"
                :disabled="readonly || !data.id"
                @click="confirmDeleteAttachment(data)"
              />
            </div>
          </template>
        </Column>
        <template #empty>
          <div style="text-align:center;padding:40px 0">
            <img src="/no_data.png" alt="No Data" style="max-width:220px;opacity:0.7" />
            <div style="color:#888;font-size:1em">No Attachments</div>
          </div>
        </template>
      </DataTable>

      <template #footer>
        <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
          <ButtonPrime type="button" :label="t('Close')" severity="secondary" raised size="small" class="px-4" @click="dialog.visible = false" />
        </div>
      </template>
    </DialogPrime>

    <!-- Dialog Preview PDF -->
    <DialogPrime
      v-model:visible="previewModal.visible"
      :header="previewModal.fileName"
      :style="{ width: '95vw', maxHeight: '95vh', margin: '10px' }"
      :pt="{
        mask: { style: 'backdrop-filter: blur(5px); background: rgba(0,0,0,0.3);' },
        content: { style: 'background: transparent; border: none; box-shadow: none;' },
        header: { style: 'background: rgba(255,255,255,0.95); backdrop-filter: blur(10px); border-radius: 12px 12px 0 0;' },
        closeButton: { style: 'background: rgba(255,255,255,0.95); backdrop-filter: blur(10px);' }
      }"
    >
      <iframe v-if="previewModal.pdfUrl" :src="previewModal.pdfUrl + '#zoom=150'" class="pdf-iframe"></iframe>
    </DialogPrime>

    <!-- Native Image Preview Trigger (Hidden) -->
    <div style="position: absolute; width: 0; height: 0; overflow: hidden;">
      <PrimeImage ref="imagePreviewer" :src="previewModal.tempImageUrl" preview @load="onPreviewImageLoad" />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import api from '@/helpers/api';
import translate from '@/helpers/translate';
import smartcatAttachmentService from '@/services/dictionary/smartcatAttachmentService';
import mcrAttachmentService from '@/services/materialCatalogingRequest/mcrAttachmentService';

export default {
  name: 'McrAttachmentDialog',
  props: {
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      dialog: {
        visible: false,
        loading: false,
        uploading: false,
        uploadProgress: 0,
        mcrCode: '',
        attachments: [],
        error: ''
      },
      previewModal: {
        visible: false,
        fileName: '',
        pdfUrl: '',
        tempImageUrl: "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
      },
    };
  },
  computed: {
    mcrCode() {
      return this.$store.state.mcr?.mcrHeader?.mcr_code || this.$route.params.mcrCode || '';
    },
  },
  watch: {
    'previewModal.visible'(val) {
      if (!val && this.previewModal.pdfUrl) {
        URL.revokeObjectURL(this.previewModal.pdfUrl);
        this.previewModal.pdfUrl = '';
      }
    }
  },
  beforeUnmount() {
    if (this.previewModal.pdfUrl) URL.revokeObjectURL(this.previewModal.pdfUrl);
    if (this.previewModal.tempImageUrl && this.previewModal.tempImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewModal.tempImageUrl);
    }
  },
  methods: {
    t(key) {
      try {
        const translated = translate.tt(key);
        if (translated && !translated.startsWith('Unknown')) return translated;
      } catch (e) { /* dictionary belum dimuat */ }
      return key;
    },
    open() {
      this.dialog.visible = true;
      this.fetchAttachments();
    },
    async fetchAttachments() {
      const mcrCode = this.mcrCode;
      this.dialog.mcrCode = mcrCode;
      if (!mcrCode) {
        this.dialog.attachments = [];
        this.dialog.error = 'MCR code not found';
        return;
      }
      this.dialog.loading = true;
      this.dialog.error = '';
      try {
        const res = await api(this, mcrAttachmentService.getAttachments(mcrCode), false);
        const list = res?.data || [];
        this.dialog.attachments = Array.isArray(list) ? list : [];
      } catch (e) {
        this.dialog.attachments = [];
        this.dialog.error = e?.response?.data?.message || 'Failed to load attachments';
      } finally {
        this.dialog.loading = false;
      }
    },
    triggerUpload() {
      this.$refs.fileInput?.click();
    },
    onFileChange(event) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      if (this.dialog.uploading) return;
      if (!this.isAllowedAttachmentFile(file)) {
        this.$toastNotifPrime('error', 'Error', 'Only PDF and image files are allowed');
        return;
      }
      this.dialog.uploading = true;
      this.uploadAttachment(file);
    },
    isAllowedAttachmentFile(file) {
      const allowedMimes = [
        'application/pdf',
        'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.oasis.opendocument.spreadsheet',
        'text/csv',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      if (file?.type && allowedMimes.includes(file.type)) return true;
      const ext = (file?.name || '').split('.').pop().toLowerCase();
      return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'xls', 'xlsx', 'csv', 'doc', 'docx'].includes(ext);
    },
    async uploadAttachment(file) {
      const mcrCode = this.mcrCode;
      if (!mcrCode) {
        this.$toastNotifPrime('error', 'Error', 'MCR code not found');
        return;
      }
      this.dialog.uploading = true;
      this.dialog.uploadProgress = 0;
      try {
        await api(this, mcrAttachmentService.uploadAttachment(mcrCode, file, (progressEvent) => {
          if (progressEvent.total) {
            this.dialog.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          }
        }), true);
        this.dialog.uploading = false;
        await this.fetchAttachments();
      } catch (e) {
        // Error toast ditangani oleh api helper
      } finally {
        this.dialog.uploading = false;
        this.dialog.uploadProgress = 0;
      }
    },
    confirmDeleteAttachment(data) {
      if (!data?.id) return;
      const fileName = data.file_attachment?.original_filename || data.file_attachment?.file_name || data.file_code || 'this attachment';
      this.confirmDialog(`Are you sure you want to delete ${fileName}?`, 'delete it').then(async (result) => {
        if (!result?.isConfirmed) return;
        try {
          await api(this, mcrAttachmentService.deleteAttachment(data.id), false);
        } catch (e) {
          this.$toastNotifPrime('error', 'Error', e?.response?.data?.message || 'Failed to delete attachment');
          return;
        }
        this.$toastNotifPrime('success', 'Success', 'Attachment deleted successfully');
        await this.fetchAttachments();
      });
    },
    getAttachmentServiceCall(data) {
      const fileAttachment = data?.file_attachment;
      if (fileAttachment?.id) return smartcatAttachmentService.file(fileAttachment.id);
      if (data?.file_code) return smartcatAttachmentService.fileByCode(data.file_code);
      return null;
    },
    async downloadAttachment(data) {
      const serviceCall = this.getAttachmentServiceCall(data);
      if (!serviceCall) return;
      const fileName = data?.file_attachment?.original_filename || data?.file_attachment?.file_name || data?.file_code || 'attachment';
      try {
        const response = await api(this, serviceCall, false);
        const blob = response instanceof Blob ? response : (response?.data instanceof Blob ? response.data : new Blob([response?.data || '']));
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } catch {
        this.$toastNotifPrime('error', 'Error', 'Failed to download file');
      }
    },
    getFileExtension(filename = '') {
      return filename.split('.').pop().toLowerCase();
    },
    isPdfFile(data, fileName = '') {
      const name = fileName || data?.file_attachment?.original_filename || data?.file_attachment?.file_name || data?.file_code || '';
      const mime = data?.file_attachment?.mime_type || '';
      return mime === 'application/pdf' || this.getFileExtension(name) === 'pdf';
    },
    isImageFile(data, fileName = '') {
      const name = fileName || data?.file_attachment?.original_filename || data?.file_attachment?.file_name || data?.file_code || '';
      const mime = data?.file_attachment?.mime_type || '';
      return mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(this.getFileExtension(name));
    },
    isPreviewableFile(data) {
      return this.isPdfFile(data) || this.isImageFile(data);
    },
    async previewAttachment(data) {
      const serviceCall = this.getAttachmentServiceCall(data);
      if (!serviceCall) return;
      const fileName = data?.file_attachment?.original_filename || data?.file_attachment?.file_name || data?.file_code || 'attachment';
      this.previewModal.fileName = fileName;
      try {
        const response = await api(this, serviceCall, false);
        const blob = response instanceof Blob ? response : (response?.data instanceof Blob ? response.data : new Blob([response?.data || '']));
        if (this.isPdfFile(data, fileName)) {
          if (this.previewModal.pdfUrl) URL.revokeObjectURL(this.previewModal.pdfUrl);
          const pdfBlob = new Blob([blob], { type: 'application/pdf' });
          this.previewModal.pdfUrl = URL.createObjectURL(pdfBlob);
          this.previewModal.visible = true;
        } else if (this.isImageFile(data, fileName)) {
          if (this.previewModal.tempImageUrl && this.previewModal.tempImageUrl.startsWith('blob:')) {
            URL.revokeObjectURL(this.previewModal.tempImageUrl);
          }
          this.previewModal.tempImageUrl = URL.createObjectURL(blob);
        }
      } catch {
        this.$toastNotifPrime('error', 'Error', 'Failed to load preview');
      }
    },
    onPreviewImageLoad() {
      if (this.previewModal.tempImageUrl && this.previewModal.tempImageUrl.startsWith('blob:')) {
        this.$nextTick(() => {
          if (this.$refs.imagePreviewer && typeof this.$refs.imagePreviewer.onImageClick === 'function') {
            this.$refs.imagePreviewer.onImageClick();
          }
        });
      }
    },
    formatFileSize(size) {
      if (size === null || size === undefined || size === '') return '-';
      const num = Number(size);
      if (Number.isNaN(num)) return '-';
      if (num >= 1024) return (num / 1024).toFixed(2) + ' MB';
      return Math.round(num) + ' KB';
    },
    formatDate(date) {
      return date ? moment(date).format('DD MMM YYYY, HH:mm') : '-';
    },
    getFileIcon(data) {
      const fileName = data?.file_attachment?.original_filename || data?.file_attachment?.file_name || data?.file_code || '';
      const mime = data?.file_attachment?.mime_type || '';
      if (mime.startsWith('image/')) return 'pi pi-image text-info';
      const ext = fileName.split('.').pop().toLowerCase();
      switch (ext) {
        case 'pdf': return 'pi pi-file-pdf text-danger';
        case 'doc':
        case 'docx': return 'pi pi-file-word text-primary';
        case 'xls':
        case 'xlsx':
        case 'csv': return 'pi pi-file-excel text-success';
        case 'ppt':
        case 'pptx': return 'pi pi-file-edit text-danger';
        case 'zip':
        case 'rar':
        case '7z': return 'pi pi-file-edit text-warning';
        case 'txt': return 'pi pi-file text-secondary';
        default: return 'pi pi-file text-primary';
      }
    },
  }
};
</script>

<style scoped>
.pdf-iframe {
  width: 100%;
  height: 80vh;
  border: none;
  border-radius: 0 0 12px 12px;
}

:deep(.centered-column-header) {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}
</style>
